import type { DashboardPoint, DashboardSlice } from '@/api/schema';
import { NOTHING } from '@/lib/nothing';

import styles from './Charts.module.css';

/**
 * Charts drawn with CSS and inline SVG rather than a charting library.
 *
 * Six shapes, none of them complicated, against a bundle that would grow by a hundred
 * kilobytes and a theming layer that would have to be taught the design tokens twice.
 * Every one of these reads its colours from the same variables everything else does.
 */

/** Shown wherever a chart has nothing to draw. */
export function NoData({ message }: { message: string }) {
  return <p className={styles.empty}>{message}</p>;
}

/** A horizontal bar per row, widths relative to the largest. */
export function Bars({ rows, empty }: { rows: DashboardSlice[]; empty: string }) {
  if (rows.length === 0) return <NoData message={empty} />;
  const largest = Math.max(...rows.map((row) => row.count), 1);

  return (
    <ul className={styles.bars}>
      {rows.map((row) => (
        <li key={row.label} className={styles.bar}>
          <span className={styles.barLabel}>{row.label}</span>
          <span className={styles.barTrack}>
            <span
              className={styles.barFill}
              style={{ inlineSize: `${(row.count / largest) * 100}%` }}
            />
          </span>
          <span className={styles.barCount}>{row.count}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The funnel, as bars that narrow.
 *
 * Widths are relative to the first step rather than the largest, so the shape carries
 * the drop-off, which is the only thing a funnel is for.
 */
export function Funnel({ steps, empty }: { steps: DashboardSlice[]; empty: string }) {
  const top = steps[0]?.count ?? 0;
  if (top === 0) return <NoData message={empty} />;

  return (
    <ol className={styles.funnel}>
      {steps.map((step, index) => {
        const share = (step.count / top) * 100;
        const previous = steps[index - 1]?.count;
        const dropped = previous !== undefined ? previous - step.count : 0;
        return (
          <li key={step.label} className={styles.step}>
            <span className={styles.stepLabel}>{step.label}</span>
            <span className={styles.stepTrack}>
              <span className={styles.stepFill} style={{ inlineSize: `${share}%` }}>
                {step.count}
              </span>
            </span>
            <span className={styles.stepDrop}>
              {index === 0 ? `${Math.round(share)}%` : dropped > 0 ? `−${dropped}` : NOTHING}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * A weekly series as an area chart.
 *
 * Drawn as one SVG path over a 0–100 viewBox with `preserveAspectRatio="none"`, so it
 * stretches to whatever width the panel has without any measuring.
 */
export function Area({ points, empty }: { points: DashboardPoint[]; empty: string }) {
  if (points.length === 0) return <NoData message={empty} />;

  const peak = Math.max(...points.map((point) => point.count), 1);
  const step = points.length > 1 ? 100 / (points.length - 1) : 0;
  const coords = points.map((point, index) => ({
    x: index * step,
    y: 100 - (point.count / peak) * 92,
  }));

  const line = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x},${c.y}`).join(' ');
  const area = `${line} L100,100 L0,100 Z`;

  return (
    <div className={styles.area}>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className={styles.areaSvg}
        role="img"
        aria-label={`${points.reduce((sum, p) => sum + p.count, 0)} candidates over ${points.length} weeks`}
      >
        <path d={area} className={styles.areaFill} />
        <path d={line} className={styles.areaLine} vectorEffect="non-scaling-stroke" />
      </svg>
      <div className={styles.areaAxis}>
        <span>{points[0]?.week}</span>
        <span>peak {peak}</span>
        <span>{points[points.length - 1]?.week}</span>
      </div>
    </div>
  );
}

/** A donut, as a single stroked circle per slice. */
export function Donut({ rows, empty }: { rows: DashboardSlice[]; empty: string }) {
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  if (total === 0) return <NoData message={empty} />;

  const radius = 40;
  const circumference = 2 * Math.PI * radius;

  // Each slice's arc length, and where it starts. Worked out before rendering rather
  // than by accumulating inside the map: a variable mutated while the list is being
  // built is a render with a memory, which React is right to refuse.
  const arcs = rows.reduce<{ row: DashboardSlice; length: number; start: number }[]>(
    (built, row) => {
      const previous = built[built.length - 1];
      const start = previous ? previous.start + previous.length : 0;
      return [...built, { row, length: (row.count / total) * circumference, start }];
    },
    [],
  );

  return (
    <div className={styles.donut}>
      <svg viewBox="0 0 100 100" className={styles.donutSvg} role="img" aria-label="Sources">
        {arcs.map((arc, index) => (
          <circle
            key={arc.row.label}
            cx="50"
            cy="50"
            r={radius}
            className={styles[`slice${(index % 5) + 1}` as keyof typeof styles]}
            strokeDasharray={`${arc.length} ${circumference - arc.length}`}
            strokeDashoffset={-arc.start}
          />
        ))}
      </svg>
      <ul className={styles.legend}>
        {rows.map((row, index) => (
          <li key={row.label} className={styles.legendItem}>
            <span
              className={`${styles.legendDot} ${styles[`dot${(index % 5) + 1}` as keyof typeof styles]}`}
            />
            {row.label}
            <b>{row.count}</b>
          </li>
        ))}
      </ul>
    </div>
  );
}
